import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { SuggestionsModal, TeamCard } from 'components/organisms/GroupOverView';
import { useInstructor } from 'contexts/instructorRole';
import { Accordion, AccordionSummary, AccordionDetails, Icon, Stack, Text } from 'shell/components';

const EmptySuggestions = () => {
    return (
        <CustomStack alignItems={'center'} spacing={2}>
            <CustomIcon kind={'checkmark'} />
            <Text color={'light'} variant={'sh1'} weight={'semibold'}>
                No new reviews right now!
            </Text>
            <Text color={'light'} variant={'p2'} weight={'semibold'}>
                All tasks are on track.
            </Text>
        </CustomStack>
    );
};

const GroupCardView = ({
    initialSuggestionsGroupId,
    groupList = [],
    suggestionsList = [],
    searchKey = '',
    statusKey,
    sortKey,
    cleanUp,
}) => {
    const { handleGroupClicked } = useInstructor();
    const [modal, setModal] = useState({
        isOpen: Boolean(initialSuggestionsGroupId),
        selectedGroupId: initialSuggestionsGroupId || '',
        category: '',
    });

    const history = useHistory();
    const getSuggestionsByType = (types) => {
        return suggestionsList.map((group) => {
            return {
                ...group,
                suggestions: group.suggestions.filter((suggestion) => types.includes(suggestion.type)),
            };
        });
    };

    const taskSuggestions = getSuggestionsByType(['task']);
    const numTaskSuggestions = taskSuggestions.reduce((acc, cur) => (cur.suggestions.length ? acc + 1 : acc), 0);
    const statusSuggestions = getSuggestionsByType(['status']);
    const numStatusSuggestions = statusSuggestions.reduce((acc, cur) => (cur.suggestions.length ? acc + 1 : acc), 0);
    const contributionSuggestions = getSuggestionsByType(['activity', 'contribution']);
    const numContributionSuggestions = contributionSuggestions.reduce(
        (acc, cur) => (cur.suggestions.length ? acc + 1 : acc),
        0
    );
    const workloadSuggestions = getSuggestionsByType(['workload']);
    const numWorkloadSuggestions = workloadSuggestions.reduce(
        (acc, cur) => (cur.suggestions.length ? acc + 1 : acc),
        0
    );

    const defaultOpenTab = numTaskSuggestions
        ? 'task'
        : numStatusSuggestions
        ? 'status'
        : numContributionSuggestions
        ? 'contribution'
        : numWorkloadSuggestions
        ? 'workload'
        : 'all';

    const handleClickGroup = (id) => {
        handleGroupClicked(id);
        return history.push({ pathname: `/overview/${id}` });
    };

    const renderCards = (category, suggestions) => {
        return groupList
            .filter((group) => {
                const passesSearchFilter = searchKey
                    ? group.name.toLowerCase().includes(searchKey.toLowerCase())
                    : true;
                const passesStatusFilter = statusKey.length ? statusKey.includes(group.status) : true;
                return passesSearchFilter && passesStatusFilter;
            })
            .sort((a, b) => {
                if (sortKey === 'titlea-z') {
                    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
                } else if (sortKey === 'titlez-a') {
                    return a.name.toLowerCase() < b.name.toLowerCase() ? 1 : -1;
                } else if (sortKey === 'highestPercentage') {
                    return b.progress - a.progress;
                } else if (sortKey === 'lowestPercentage') {
                    return a.progress - b.progress;
                }
            })
            .map((group) => {
                return (
                    <TeamCard
                        key={group.id}
                        category={category}
                        groupName={group.name}
                        progress={group.progress}
                        status={group.status}
                        suggestions={
                            suggestions.find((suggestion) => suggestion.group_id === group.id)?.suggestions || []
                        }
                        onClickViewGroupDetail={() => handleClickGroup(group.id)}
                        onClickViewAllByType={(category) =>
                            setModal({ isOpen: true, selectedGroupId: group.id, category })
                        }
                    />
                );
            });
    };

    useEffect(() => {
        return () => cleanUp();
    }, []);

    return (
        <>
            <Stack spacing={4}>
                <CustomAccordion defaultExpanded={defaultOpenTab === 'task' || defaultOpenTab === 'all'}>
                    <AccordionSummary>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Task Alerts ({numTaskSuggestions})
                        </Text>
                    </AccordionSummary>
                    <AccordionDetails>
                        {numTaskSuggestions ? (
                            <Stack sx={{ flexWrap: 'wrap' }} direction={'row'} spacing={4}>
                                {renderCards('task', taskSuggestions)}
                            </Stack>
                        ) : (
                            <EmptySuggestions />
                        )}
                    </AccordionDetails>
                </CustomAccordion>
                <CustomAccordion defaultExpanded={defaultOpenTab === 'status' || defaultOpenTab === 'all'}>
                    <AccordionSummary>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Progress Status ({numStatusSuggestions})
                        </Text>
                    </AccordionSummary>
                    <AccordionDetails>
                        {numStatusSuggestions ? (
                            <Stack sx={{ flexWrap: 'wrap' }} direction={'row'} spacing={4}>
                                {renderCards('status', statusSuggestions)}
                            </Stack>
                        ) : (
                            <EmptySuggestions />
                        )}
                    </AccordionDetails>
                </CustomAccordion>
                <CustomAccordion defaultExpanded={defaultOpenTab === 'contribution' || defaultOpenTab === 'all'}>
                    <AccordionSummary>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Contribution ({numContributionSuggestions})
                        </Text>
                    </AccordionSummary>
                    <AccordionDetails>
                        {numContributionSuggestions ? (
                            <Stack sx={{ flexWrap: 'wrap' }} direction={'row'} spacing={4}>
                                {renderCards('contribution', contributionSuggestions)}
                            </Stack>
                        ) : (
                            <EmptySuggestions />
                        )}
                    </AccordionDetails>
                </CustomAccordion>
                <CustomAccordion defaultExpanded={defaultOpenTab === 'workload' || defaultOpenTab === 'all'}>
                    <AccordionSummary>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Workload ({numWorkloadSuggestions})
                        </Text>
                    </AccordionSummary>
                    <AccordionDetails>
                        {numWorkloadSuggestions ? (
                            <Stack sx={{ flexWrap: 'wrap' }} direction={'row'} spacing={4}>
                                {renderCards('workload', workloadSuggestions)}
                            </Stack>
                        ) : (
                            <EmptySuggestions />
                        )}
                    </AccordionDetails>
                </CustomAccordion>
            </Stack>
            {modal.isOpen &&
                (() => {
                    const group = groupList.find(({ id }) => id === modal.selectedGroupId);
                    const suggestions = suggestionsList
                        .find(({ group_id }) => group_id === modal.selectedGroupId)
                        .suggestions.filter((suggestion) => {
                            if (!modal.category) return true;
                            const allowedSuggestionType =
                                modal.category === 'contribution' ? ['activity', 'contribution'] : [modal.category];
                            return allowedSuggestionType.includes(suggestion.type);
                        });

                    return (
                        <SuggestionsModal
                            visible={modal.isOpen}
                            width={325}
                            groupName={group.name}
                            groupStatus={group.status}
                            groupProgress={group.progress}
                            suggestions={suggestions}
                            onClose={() => setModal({ isOpen: false, selectedGroupId: '', category: '' })}
                        />
                    );
                })()}
        </>
    );
};

const CustomAccordion = styled(Accordion)(({ theme }) => {
    return {
        background: 'inherit',
        '& > .MuiAccordionSummary-root': {
            width: 'fit-content',
        },
        '& > .MuiAccordionSummary-root > .MuiAccordionSummary-content': {
            marginRight: theme.spacing(2),
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px',
        width: '100%',
        padding: theme.spacing(5, 0, 6),
        background: theme.palette.ensightful.neutral[100],
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background: theme.palette.ensightful.neutral[600],
        color: theme.palette.ensightful.shades[0],
    };
});

export default GroupCardView;
